/**
 * SearchInConversation
 *
 * Search bar that filters messages within the current conversation.
 * Shows results count and navigation arrows.
 */

import { useState, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

export default function SearchInConversation({ messages, onHighlight, onClose }) {
  const [query, setQuery] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const results = query.length >= 2
    ? messages.filter(m =>
        m.text?.toLowerCase().includes(query.toLowerCase())
      )
    : [];

  const handleSearch = useCallback((value) => {
    setQuery(value);
    setCurrentIndex(0);
    if (value.length >= 2) {
      const matches = messages.filter(m =>
        m.text?.toLowerCase().includes(value.toLowerCase())
      );
      if (matches.length > 0) {
        onHighlight?.(matches[0].id);
      }
    }
  }, [messages, onHighlight]);

  const goToResult = (index) => {
    if (results.length === 0) return;
    const safeIndex = ((index % results.length) + results.length) % results.length;
    setCurrentIndex(safeIndex);
    onHighlight?.(results[safeIndex].id);
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b border-base-300 bg-base-200">
      <MagnifyingGlassIcon className="w-4 h-4 text-base-content/40 flex-shrink-0" />
      <input
        type="search"
        placeholder="Search in conversation..."
        value={query}
        onChange={(e) => handleSearch(e.target.value)}
        className="input input-sm input-ghost flex-1 bg-transparent"
        autoFocus
      />
      {query.length >= 2 && (
        <span className="text-xs text-base-content/50 flex-shrink-0">
          {results.length > 0 ? `${currentIndex + 1}/${results.length}` : 'No results'}
        </span>
      )}
      {results.length > 1 && (
        <div className="flex gap-0.5">
          <button className="btn btn-ghost btn-xs btn-circle" onClick={() => goToResult(currentIndex - 1)}>
            <ChevronUpIcon className="w-3 h-3" />
          </button>
          <button className="btn btn-ghost btn-xs btn-circle" onClick={() => goToResult(currentIndex + 1)}>
            <ChevronDownIcon className="w-3 h-3" />
          </button>
        </div>
      )}
      <button className="btn btn-ghost btn-xs btn-circle" onClick={onClose}>
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
